import { useNavigate } from 'react-router-dom';
import { Edit, Eye, Trash2, Layers, Code } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@aaas/shared';
import type { Widget } from '../types/widget.types';

interface WidgetCardProps {
  widget: Widget;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export function WidgetCard({ widget, onEdit, onDelete }: WidgetCardProps) {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    navigate(`/kms/widgets/${widget.id}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card
      className="cursor-pointer transition-all duration-200 hover:shadow-md group h-full flex flex-col"
      onClick={handleCardClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg text-gray-900 truncate mb-1 flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full border flex-shrink-0"
                style={{ backgroundColor: widget.theme_color }}
                title={`Theme color: ${widget.theme_color}`}
              />
              {widget.name}
            </CardTitle>
          </div>
          <Badge variant={widget.is_active ? 'default' : 'secondary'} className="flex-shrink-0">
            {widget.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-0 flex flex-col flex-grow">
        <div className="flex flex-col h-full">
          {/* Description - fixed height area */}
          <div className="flex-grow mb-3">
            {widget.description ? (
              <div className="text-sm text-gray-600 line-clamp-2">{widget.description}</div>
            ) : (
              <div className="text-sm text-gray-400 italic">No description provided</div>
            )}
          </div>

          {/* Widget Info */}
          <div className="flex items-center gap-3 mb-3 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <Layers className="h-3 w-3" />
              <span>
                {widget.spaces?.length ?? 0} space{(widget.spaces?.length ?? 0) === 1 ? '' : 's'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Code className="h-3 w-3" />
              <span>{widget.position}</span>
            </div>
          </div>

          {/* Bottom section - always at bottom */}
          <div className="mt-auto space-y-3">
            {/* Dates */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Created {formatDate(widget.created_at)}</span>
              <span>Updated {formatDate(widget.updated_at)}</span>
            </div>

            {/* Action Icons */}
            <div className="flex items-center justify-center gap-1 pt-2 border-t border-gray-100">
              {/* View */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/kms/widgets/${widget.id}`);
                }}
                title="View Widget"
              >
                <Eye className="h-4 w-4" />
              </Button>

              {/* Edit */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(widget.id);
                }}
                title="Edit Widget"
              >
                <Edit className="h-4 w-4" />
              </Button>

              {/* Delete */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(widget.id);
                }}
                title="Delete Widget"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
